import { useInput } from "@redotech/react-util/form";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { useAddressAutocompleteActions } from "@redotech/redo-customer-portal-shared/hooks/useAddressAutocomplete/use-address-autocomplete-actions";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoShopifyCountryCode } from "@redotech/redo-model/fulfillments/fulfillment-country-code";
import {
  AddressAutocompleteForm,
  createAddressAutocompleteForm,
} from "@redotech/redo-web/address-autocomplete-form";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { InputProvider } from "@redotech/ui/form";
import { memo, useEffect, useMemo } from "react";
import { StickyFooter } from "../submission/components/sticky-footer";

export interface EditAddressModalData {
  initialValues: {
    name: string;
    address1: string;
    address2: string | null;
    city: string;
    province: string | null;
    zip: string;
    countryCode: RedoShopifyCountryCode;
    email: string | null;
    phone: string | null;
    company: string | null;
  };
  texts: { shipItemsTo: string; title: string };
  showEditAddressLoading: false;
}

export interface EditAddressModalProps {
  data: EditAddressModalData | { showEditAddressLoading: true };
  onConfirm: (
    formValues: InputProvider.Value<
      ReturnType<typeof createAddressAutocompleteForm>
    >,
  ) => Promise<void>;
  onClose: () => void;
  saving?: boolean;
  error?: string;
  uiSelector?: string;
}

export const EditAddressModal = memo(function EditAddressModal({
  data,
  onConfirm,
  onClose,
  saving,
  error,
  uiSelector,
}: EditAddressModalProps) {
  const { t } = useTranslation();
  const screenSize = useScreenType();
  const actions = useAddressAutocompleteActions();

  const defaultFormValues = {
    name: "",
    address1: "",
    address2: null,
    city: "",
    province: null,
    zip: "",
    countryCode: "US" as RedoShopifyCountryCode,
    email: null,
    phone: null,
    company: null,
  };

  const formSchema = useMemo(() => createAddressAutocompleteForm(true), []);
  const form = useInput(formSchema, defaultFormValues);

  useEffect(() => {
    // Properly sets initial values after loading them
    if (!data.showEditAddressLoading && data.initialValues) {
      form.setValue(data.initialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.showEditAddressLoading]);

  const isFormValid = useMemo(() => {
    return (
      form.errors.length === 0 &&
      form.value.name.trim() &&
      form.value.address1.trim() &&
      form.value.city.trim() &&
      form.value.zip.trim() &&
      form.value.email?.trim()
    );
  }, [form.errors, form.value]);

  const handleConfirm = async () => {
    if (isFormValid) {
      await onConfirm(form.value);
    }
  };

  if (data.showEditAddressLoading) {
    return <LoadingRedoAnimation />;
  }

  const {
    texts: { shipItemsTo, title },
  } = data;

  return (
    <Flex
      data-ui={uiSelector}
      dir="column"
      gap="2xl"
      pt="3xl"
      px="3xl"
      style={
        screenSize === ScreenType.DESKTOP ? { width: "min(500px, 80vw)" } : {}
      }
    >
      <Text fontSize="lg" fontWeight="semibold" textColor="primary">
        {title}
      </Text>
      <Divider />
      <Flex dir="column" gap="3xl" pt="sm">
        <Text fontSize="md" textColor="tertiary">
          {shipItemsTo}
        </Text>

        <AddressAutocompleteForm
          actions={actions}
          autoCompleteOnName={false}
          form={form}
          hideCompanyField
          hideEmailField={false}
          includeNameField
          requireEmail
          showRequiredLabels
          useLegacyProvinceSelect
        />
      </Flex>
      {error && (
        <Text fontSize="sm" textColor="error">
          {error}
        </Text>
      )}
      <StickyFooter>
        <Flex dir="column" gap="3xl" pt="sm">
          <Divider />

          <Flex justify="flex-end">
            <RedoButton
              disabled={saving}
              hierarchy="secondary"
              onClick={onClose}
              size="md"
              text={t("Cancel")}
            />
            <RedoButton
              dataTestId="save-button"
              disabled={!isFormValid || saving}
              hierarchy="primary"
              onClick={handleConfirm}
              pending={saving}
              size="md"
              text={t("Save")}
            />
          </Flex>
        </Flex>
      </StickyFooter>
    </Flex>
  );
});
